import { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Header } from '@/components/Header';
import { BottomNav } from '@/components/BottomNav';
import { LeaderboardCard } from '@/components/LeaderboardCard';
import { TradesTab } from '@/components/TradesTab';
import { BonusMalusTab } from '@/components/BonusMalusTab';
import { GiornateTab } from '@/components/GiornateTab';
import { ChampionshipStandingCard } from '@/components/ChampionshipStandingCard';
import { AdminTab } from '@/components/AdminTab';
import { WhatsAppTab } from '@/components/WhatsAppTab';
import { StatsTab } from '@/components/StatsTab';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ArrowLeft, Plus, Copy, Users, Trophy, Settings, Shield, Crown, UserPlus, GraduationCap, TrendingUp, TrendingDown, Loader2, MoreVertical, LogOut, Trash2, Edit, UserCog, ArrowRightLeft, Calendar, Lock, MessageCircle } from 'lucide-react';
import { toast } from 'sonner';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const LeagueDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token } = useAuth();
  
  const [league, setLeague] = useState(null);
  const [professors, setProfessors] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [championshipStandings, setChampionshipStandings] = useState([]);
  const [rules, setRules] = useState(null);
  const [customBonusMalus, setCustomBonusMalus] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Ref per refreshare GiornateTab dopo aggiunta eventi
  const giornateTabRef = useRef(null);
  
  // Dialog states
  const [showAddProfessor, setShowAddProfessor] = useState(false);
  const [showCreateTeam, setShowCreateTeam] = useState(false);
  const [showAddEvent, setShowAddEvent] = useState(false);
  const [showPromoteAdmin, setShowPromoteAdmin] = useState(false);
  const [showAssignProfessor, setShowAssignProfessor] = useState(false);
  const [showEditTeam, setShowEditTeam] = useState(false);
  const [showAddOwner, setShowAddOwner] = useState(false);
  const [showEditProfessor, setShowEditProfessor] = useState(false);
  const [showLeaveLeague, setShowLeaveLeague] = useState(false);
  const [showDeleteLeague, setShowDeleteLeague] = useState(false);
  
  // Form states
  const [profName, setProfName] = useState('');
  const [profSubject, setProfSubject] = useState('');
  const [teamName, setTeamName] = useState('');
  const [isTeamDuo, setIsTeamDuo] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState('');
  const [selectedProfessor, setSelectedProfessor] = useState('');
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedMember, setSelectedMember] = useState('');
  const [selectedTeam, setSelectedTeam] = useState('');
  const [professorToAssign, setProfessorToAssign] = useState(null);
  const [teamToEdit, setTeamToEdit] = useState(null);
  const [editTeamName, setEditTeamName] = useState('');
  const [editPartner, setEditPartner] = useState('none');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [memberToPromoteOwner, setMemberToPromoteOwner] = useState('');
  const [professorToEdit, setProfessorToEdit] = useState(null);
  const [editProfName, setEditProfName] = useState('');
  const [editProfSubject, setEditProfSubject] = useState('');
  const [eventMultiplier, setEventMultiplier] = useState(1); // Quantità moltiplicatore per bonus/malus

  const fetchLeague = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leagues/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setLeague(await response.json());
      } else {
        navigate('/leagues');
      }
    } catch (err) {
      toast.error('Errore nel caricamento della lega');
    }
  }, [id, token, navigate]);

  const fetchProfessors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/professors/${id}?t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
      });
      if (response.ok) {
        setProfessors(await response.json());
      }
    } catch (err) {
      console.error('Error fetching professors:', err);
    }
  }, [id, token]);

  const fetchTeams = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/teams/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (response.ok) {
        setTeams(await response.json());
      }
    } catch (err) {
      console.error('Error fetching teams:', err);
    }
  }, [id, token]);

  const fetchLeaderboard = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leaderboard/${id}?t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
      });
      if (response.ok) {
        setLeaderboard(await response.json());
      }
    } catch (err) {
      console.error('Error fetching leaderboard:', err);
    }
  }, [id, token]);

  const fetchChampionshipStandings = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/leagues/${id}/championship-standings?t=${Date.now()}`, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache'
        },
      });
      if (response.ok) {
        setChampionshipStandings(await response.json());
      }
    } catch (err) {
      console.error('Error fetching championship standings:', err);
    }
  }, [id, token]);

  const fetchRules = useCallback(async () => {
    try {
      // Fetch ALL bonus/malus for this league (default + custom, filtered by backend)
      // This endpoint already filters out hidden items correctly
      if (id) {
        const customResponse = await fetch(`${API_URL}/api/leagues/${id}/bonus-malus`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (customResponse.ok) {
          const allBonusMalus = await customResponse.json();
          setCustomBonusMalus(allBonusMalus); // Include sia default che custom (backend filtra is_hidden)
        }
      }
    } catch (err) {
      console.error('Error fetching rules:', err);
    }
  }, [id, token]);

  const fetchAll = useCallback(async () => {
    setIsLoading(true);
    await Promise.all([
      fetchLeague(),
      fetchProfessors(),
      fetchTeams(),
      fetchLeaderboard(),
      fetchChampionshipStandings(),
      fetchRules(),
    ]);
    setIsLoading(false);
  }, [fetchLeague, fetchProfessors, fetchTeams, fetchLeaderboard, fetchChampionshipStandings, fetchRules]);

  // Callback per aggiornare classifica dopo calcolo giornata
  const handleMatchdayCalculated = useCallback(async () => {
    try {
      await Promise.all([
        fetchChampionshipStandings(),
        fetchLeaderboard(),
        fetchTeams()
      ]);
    } catch (err) {
      console.error('Error refreshing after matchday calculation:', err);
    }
  }, [fetchChampionshipStandings, fetchLeaderboard, fetchTeams]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const isAdmin = league?.admins?.includes(user?.id);
  const isOwner = league?.owners?.includes(user?.id) || league?.owner_id === user?.id;
  const hasTeam = teams.some(t => t.owner_id === user?.id || t.partner_id === user?.id);

  const handleAddProfessor = async (e) => {
    e.preventDefault();
    if (!profName || !profSubject) {
      toast.error('Compila tutti i campi');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/professors`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: profName,
          subject: profSubject,
          league_id: id,
        }),
      });

      if (response.ok) {
        const newProf = await response.json();
        setProfessors([...professors, newProf]);
        setShowAddProfessor(false);
        setProfName('');
        setProfSubject('');
        toast.success('Professore aggiunto!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCreateTeam = async (e) => {
    e.preventDefault();
    if (!teamName) {
      toast.error('Inserisci un nome per il team');
      return;
    }

    if (isTeamDuo && !selectedPartner) {
      toast.error('Seleziona un partner per giocare in coppia');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/teams`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: teamName,
          league_id: id,
          is_duo: isTeamDuo,
          partner_id: isTeamDuo ? selectedPartner : null,
        }),
      });

      if (response.ok) {
        await fetchAll();
        setShowCreateTeam(false);
        setTeamName('');
        setIsTeamDuo(false);
        setSelectedPartner('');
        toast.success('Team creato!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddEvent = async () => {
    if (!selectedProfessor || !selectedEvent) {
      toast.error('Seleziona professore ed evento');
      return;
    }

    if (!eventMultiplier || eventMultiplier < 1) {
      toast.error('La quantità deve essere almeno 1');
      return;
    }

    setIsSubmitting(true);
    try {
      const finalPoints = selectedEvent.points * eventMultiplier;
      const description = eventMultiplier > 1 
        ? `${eventMultiplier}x ${selectedEvent.name}` 
        : selectedEvent.name;

      const response = await fetch(`${API_URL}/api/events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          professor_id: selectedProfessor,
          event_type: selectedEvent.points > 0 ? 'bonus' : 'malus',
          points: finalPoints,
          description: description,
        }),
      });

      if (response.ok) {
        // Refresh solo professori e classifica per update immediato
        await Promise.all([fetchProfessors(), fetchLeaderboard(), fetchChampionshipStandings()]);
        
        // Refresh anche GiornateTab per aggiornare i punti nella prossima giornata
        if (giornateTabRef.current?.refreshMatchdays) {
          giornateTabRef.current.refreshMatchdays();
        }
        
        setShowAddEvent(false);
        setSelectedProfessor('');
        setSelectedEvent(null);
        setEventMultiplier(1);
        toast.success(selectedEvent.points > 0 ? 'Bonus aggiunto!' : 'Malus aggiunto!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePromoteAdmin = async () => {
    if (!selectedMember) {
      toast.error('Seleziona un membro');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${id}/promote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: selectedMember }),
      });

      if (response.ok) {
        await fetchLeague();
        setShowPromoteAdmin(false);
        setSelectedMember('');
        toast.success('Utente promosso ad admin!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAssignProfessor = async () => {
    if (!selectedTeam || !professorToAssign) {
      toast.error('Seleziona un team');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/professors/${professorToAssign.id}/assign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ team_id: selectedTeam }),
      });

      if (response.ok) {
        await fetchAll();
        setShowAssignProfessor(false);
        setSelectedTeam('');
        setProfessorToAssign(null);
        toast.success('Professore assegnato al team!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteTeam = async (teamId) => {
    if (!confirm('Sei sicuro di voler eliminare questo team?')) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/${teamId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchAll();
        toast.success('Team eliminato con successo!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore durante l\'eliminazione del team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleEditTeam = async () => {
    if (!editTeamName) {
      toast.error('Inserisci un nome per il team');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/${teamToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editTeamName,
          partner_id: editPartner === "none" ? null : editPartner || null,
        }),
      });

      if (response.ok) {
        await fetchAll();
        setShowEditTeam(false);
        setTeamToEdit(null);
        setEditTeamName('');
        setEditPartner('none');
        toast.success('Team modificato con successo!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore durante la modifica del team');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUnassignProfessor = async (teamId, professorId, professorName) => {
    if (!confirm(`Sei sicuro di voler rimuovere ${professorName} da questo team?`)) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/teams/${teamId}/unassign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ professor_id: professorId }),
      });

      if (response.ok) {
        await fetchAll();
        toast.success('Professore rimosso dal team!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore durante la rimozione del professore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddOwner = async (e) => {
    e.preventDefault();
    if (!memberToPromoteOwner) {
      toast.error('Seleziona un membro');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${id}/add-owner`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ user_id: memberToPromoteOwner }),
      });

      if (response.ok) {
        await fetchLeague();
        setShowAddOwner(false);
        setMemberToPromoteOwner('');
        toast.success('Owner aggiunto!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeaveLeague = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${id}/leave`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Sei uscito dalla lega');
        navigate('/leagues');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
      setShowLeaveLeague(false);
    }
  };

  const handleDeleteLeague = async () => {
    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        toast.success('Lega eliminata');
        navigate('/leagues');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
      setShowDeleteLeague(false);
    }
  };

  const handleEditProfessor = async (e) => {
    e.preventDefault();
    if (!editProfName || !editProfSubject) {
      toast.error('Compila tutti i campi');
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/professors/${professorToEdit.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: editProfName,
          subject: editProfSubject,
        }),
      });

      if (response.ok) {
        await fetchProfessors();
        setShowEditProfessor(false);
        setProfessorToEdit(null);
        setEditProfName('');
        setEditProfSubject('');
        toast.success('Professore modificato!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProfessor = async (professorId, professorName) => {
    if (!confirm(`Sei sicuro di voler eliminare ${professorName}? Questa azione non può essere annullata.`)) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/professors/${professorId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchAll();
        toast.success('Professore eliminato!');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!confirm(`Sei sicuro di voler espellere ${memberName} dalla lega? Il suo team verrà eliminato.`)) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${id}/members/${memberId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchAll();
        toast.success('Membro espulso dalla lega');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore nell\'espulsione del membro');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRemoveAdmin = async (memberId, memberName) => {
    if (!confirm(`Sei sicuro di voler rimuovere i privilegi di admin da ${memberName}?`)) return;

    setIsSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/leagues/${id}/remove-admin/${memberId}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        await fetchAll();
        toast.success('Privilegi admin rimossi');
      } else {
        const error = await response.json();
        toast.error(error.detail);
      }
    } catch (err) {
      toast.error('Errore nella rimozione dei privilegi admin');
    } finally {
      setIsSubmitting(false);
    }
  };


  const copyInviteCode = () => {
    navigator.clipboard.writeText(league?.invite_code);
    toast.success('Codice copiato!');
  };

  const copyInviteLink = () => {
    const inviteLink = `${window.location.origin}/leagues?join=${league?.invite_code}`;
    navigator.clipboard.writeText(inviteLink);
    toast.success('Link di invito copiato!');
  };

  const getInitials = (name) => {
    if (!name) return '??';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  // Simplified to avoid Babel plugin stack overflow
  const getBonusEvents = () => {
    // Usa solo customBonusMalus che ora contiene TUTTI i bonus (default + custom)
    // Backend già filtra i default nascosti
    return customBonusMalus
      .filter(item => item.type === 'bonus')
      .map(item => ({
        name: item.name,
        points: item.points
      }));
  };

  const getMalusEvents = () => {
    // Usa solo customBonusMalus che ora contiene TUTTI i malus (default + custom)
    // Backend già filtra i default nascosti
    return customBonusMalus
      .filter(item => item.type === 'malus')
      .map(item => ({
        name: item.name,
        points: item.points
      }));
  };

  if (isLoading || !league) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 bg-background/80 backdrop-blur-lg border-b">
        <div className="flex items-center gap-3 h-14 px-4 max-w-5xl mx-auto">
          <Button variant="ghost" size="icon" onClick={() => navigate('/leagues')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1 min-w-0">
            <h1 className="font-bold truncate" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {league.name}
            </h1>
            <div className="flex items-center gap-2">
              <span className="text-xs text-muted-foreground">{league.members.length} membri</span>
              <Button variant="ghost" size="sm" className="h-5 px-2 text-xs" onClick={copyInviteCode}>
                <Copy className="w-3 h-3 mr-1" />
                {league.invite_code}
              </Button>
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon">
                <MoreVertical className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              {(isOwner || isAdmin) && (
                <>
                  <DropdownMenuItem onClick={copyInviteLink}>
                    <Copy className="w-4 h-4 mr-2" />
                    Condividi Link
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              {isOwner && (
                <>
                  <DropdownMenuItem onClick={() => setShowPromoteAdmin(true)}>
                    <Shield className="w-4 h-4 mr-2" />
                    Promuovi Admin
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowAddOwner(true)}>
                    <Crown className="w-4 h-4 mr-2" />
                    Aggiungi Owner
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteLeague(true)}
                    className="text-red-600"
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Elimina Lega
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => setShowLeaveLeague(true)}>
                <LogOut className="w-4 h-4 mr-2" />
                Esci dalla Lega
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Dialog Promuovi Admin */}
      <Dialog open={showPromoteAdmin} onOpenChange={setShowPromoteAdmin}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Promuovi Admin</DialogTitle>
            <DialogDescription>Seleziona un membro da promuovere ad admin</DialogDescription>
          </DialogHeader>
          <Select value={selectedMember} onValueChange={setSelectedMember}>
            <SelectTrigger>
              <SelectValue placeholder="Seleziona membro" />
            </SelectTrigger>
            <SelectContent>
              {league?.members
                .filter(m => m.id !== user?.id && !league.admins.includes(m.id))
                .map(m => (
                  <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button onClick={handlePromoteAdmin} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Promuovi'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Aggiungi Owner */}
      <Dialog open={showAddOwner} onOpenChange={setShowAddOwner}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Aggiungi Owner</DialogTitle>
            <DialogDescription>Seleziona un membro da promuovere a owner</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleAddOwner}>
            <Select value={memberToPromoteOwner} onValueChange={setMemberToPromoteOwner}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona membro" />
              </SelectTrigger>
              <SelectContent>
                {league?.members
                  .filter(m => m.id !== user?.id && !league.owners?.includes(m.id))
                  .map(m => (
                    <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <DialogFooter className="mt-4">
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiungi Owner'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Esci dalla Lega */}
      <Dialog open={showLeaveLeague} onOpenChange={setShowLeaveLeague}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Esci dalla Lega</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler uscire dalla lega? Il tuo team verrà eliminato.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLeaveLeague(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleLeaveLeague} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Esci'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Elimina Lega */}
      <Dialog open={showDeleteLeague} onOpenChange={setShowDeleteLeague}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Elimina Lega</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare questa lega? Questa azione è irreversibile e cancellerà tutti i team, professori ed eventi.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteLeague(false)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDeleteLeague} disabled={isSubmitting}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Elimina'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Assegna Professore a Team */}
      <Dialog open={showAssignProfessor} onOpenChange={setShowAssignProfessor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assegna Professore</DialogTitle>
            <DialogDescription>
              Assegna {professorToAssign?.name} a un team
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <Select value={selectedTeam} onValueChange={setSelectedTeam}>
              <SelectTrigger>
                <SelectValue placeholder="Seleziona team" />
              </SelectTrigger>
              <SelectContent>
                {teams.map(team => (
                  <SelectItem key={team.id} value={team.id}>
                    {team.name} {team.is_duo ? '(Coppia)' : '(Singolo)'}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button onClick={handleAssignProfessor} disabled={isSubmitting || !selectedTeam}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Assegna'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Team */}
      <Dialog open={showEditTeam} onOpenChange={setShowEditTeam}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Team</DialogTitle>
            <DialogDescription>
              Modifica il nome del team o aggiungi/cambia il partner
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Team</Label>
              <Input 
                placeholder="Nome del team" 
                value={editTeamName} 
                onChange={(e) => setEditTeamName(e.target.value)} 
              />
            </div>
            {teamToEdit?.is_duo && (
              <div className="space-y-2">
                <Label>Partner (opzionale)</Label>
                <Select value={editPartner || "none"} onValueChange={setEditPartner}>
                  <SelectTrigger>
                    <SelectValue placeholder="Seleziona un partner" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Nessun partner</SelectItem>
                    {league?.members
                      ?.filter(m => m.id !== user?.id)
                      ?.map(member => (
                        <SelectItem key={member.id} value={member.id}>
                          {member.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button onClick={handleEditTeam} disabled={isSubmitting || !editTeamName}>
              {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva Modifiche'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Modifica Professore */}
      <Dialog open={showEditProfessor} onOpenChange={setShowEditProfessor}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifica Professore</DialogTitle>
            <DialogDescription>
              Modifica il nome o la materia del professore
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEditProfessor}>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nome Professore</Label>
                <Input 
                  placeholder="Nome" 
                  value={editProfName} 
                  onChange={(e) => setEditProfName(e.target.value)} 
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Materia</Label>
                <Input 
                  placeholder="Materia" 
                  value={editProfSubject} 
                  onChange={(e) => setEditProfSubject(e.target.value)} 
                  required
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="submit" disabled={isSubmitting || !editProfName || !editProfSubject}>
                {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Salva Modifiche'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <main className="main-content">
        <Tabs defaultValue="classifica" className="w-full">
          <div className="sticky top-14 z-30 bg-background border-b overflow-x-auto">
            <TabsList className="w-full h-12 rounded-none bg-transparent border-0 inline-flex min-w-full">
              <TabsTrigger value="classifica" className="flex-1 min-w-[110px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                <Trophy className="w-4 h-4 mr-2" />
                Classifica
              </TabsTrigger>
              <TabsTrigger value="giornate" className="flex-1 min-w-[110px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                <Calendar className="w-4 h-4 mr-2" />
                Giornate
              </TabsTrigger>
              <TabsTrigger value="professori" className="flex-1 min-w-[120px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                <GraduationCap className="w-4 h-4 mr-2" />
                Professori
              </TabsTrigger>
              <TabsTrigger value="scambi" className="flex-1 min-w-[100px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Scambi
              </TabsTrigger>
              <TabsTrigger value="membri" className="flex-1 min-w-[100px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                <Users className="w-4 h-4 mr-2" />
                Membri
              </TabsTrigger>
              <TabsTrigger value="punti" className="flex-1 min-w-[90px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                <Settings className="w-4 h-4 mr-2" />
                Punti
              </TabsTrigger>
              <TabsTrigger value="statistiche" className="flex-1 min-w-[110px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                <TrendingUp className="w-4 h-4 mr-2" />
                Statistiche
              </TabsTrigger>
              {isOwner && (
                <TabsTrigger value="whatsapp" className="flex-1 min-w-[100px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                  <MessageCircle className="w-4 h-4 mr-2" />
                  WhatsApp
                </TabsTrigger>
              )}
              {isAdmin && (
                <TabsTrigger value="admin" className="flex-1 min-w-[90px] data-[state=active]:border-b-2 data-[state=active]:border-primary rounded-none whitespace-nowrap">
                  <Lock className="w-4 h-4 mr-2" />
                  Admin
                </TabsTrigger>
              )}
            </TabsList>
          </div>

          {/* Classifica Tab */}
          <TabsContent value="classifica" className="p-4 max-w-2xl mx-auto">
            {!hasTeam && (
              <Card className="mb-4 border-primary/50 bg-primary/5">
                <CardContent className="p-4 flex items-center justify-between">
                  <div>
                    <p className="font-semibold">Crea il tuo team!</p>
                    <p className="text-sm text-muted-foreground">Per apparire in classifica</p>
                  </div>
                  <Dialog open={showCreateTeam} onOpenChange={setShowCreateTeam}>
                    <DialogTrigger asChild>
                      <Button size="sm" className="rounded-full">
                        <Plus className="w-4 h-4 mr-1" />
                        Crea Team
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Crea il tuo Team</DialogTitle>
                        <DialogDescription>Scegli un nome per il tuo team</DialogDescription>
                      </DialogHeader>
                      <form onSubmit={handleCreateTeam}>
                        <div className="space-y-4 py-4">
                          <div className="space-y-2">
                            <Label>Nome Team</Label>
                            <Input 
                              placeholder="es. I Campioni" 
                              value={teamName} 
                              onChange={(e) => setTeamName(e.target.value)} 
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <input 
                              type="checkbox" 
                              id="is-duo" 
                              checked={isTeamDuo}
                              onChange={(e) => setIsTeamDuo(e.target.checked)}
                              className="rounded"
                            />
                            <Label htmlFor="is-duo" className="text-sm">Gioco in coppia</Label>
                          </div>
                          {isTeamDuo && (
                            <div className="space-y-2">
                              <Label>Seleziona Partner</Label>
                              <Select value={selectedPartner} onValueChange={setSelectedPartner}>
                                <SelectTrigger>
                                  <SelectValue placeholder="Scegli il tuo partner" />
                                </SelectTrigger>
                                <SelectContent>
                                  {league?.members
                                    ?.filter(m => m.id !== user?.id)
                                    ?.map(member => (
                                      <SelectItem key={member.id} value={member.id}>
                                        {member.name}
                                      </SelectItem>
                                    ))}
                                </SelectContent>
                              </Select>
                            </div>
                          )}
                        </div>
                        <DialogFooter>
                          <Button type="submit" disabled={isSubmitting}>
                            {isSubmitting ? 'Creazione...' : 'Crea Team'}
                          </Button>
                        </DialogFooter>
                      </form>
                    </DialogContent>
                  </Dialog>
                </CardContent>
              </Card>
            )}

            {/* Championship Standings - New Style */}
            {championshipStandings.length > 0 ? (
              <div className="space-y-3 stagger-children">
                <div className="text-center mb-4">
                  <h3 className="text-lg font-bold">Classifica Campionato</h3>
                  <p className="text-sm text-muted-foreground">Vittoria: +3 | Pareggio: +1 | Sconfitta: 0</p>
                </div>
                {championshipStandings.map((standing) => (
                  <div key={standing.team_id} className="space-y-2">
                    <ChampionshipStandingCard
                      position={standing.position}
                      teamName={standing.team_name}
                      ownerName={standing.owner_name}
                      partnerName={standing.partner_name}
                      matchesPlayed={standing.matches_played}
                      wins={standing.wins}
                      draws={standing.draws}
                      losses={standing.losses}
                      championshipPoints={standing.championship_points}
                      totalFantasyPoints={standing.total_fantasy_points}
                      isCurrentUser={standing.team_name === teams.find(t => t.owner_id === user?.id || t.partner_id === user?.id)?.name}
                    />
                    {/* Mostra bottoni solo se sei il proprietario o partner del team */}
                    {teams.find(t => t.id === standing.team_id && (t.owner_id === user?.id || t.partner_id === user?.id)) && (
                      <div className="flex gap-2 px-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => {
                            const team = teams.find(t => t.id === standing.team_id);
                            setTeamToEdit(team);
                            setEditTeamName(team.name);
                            setEditPartner(team.partner_id || 'none');
                            setShowEditTeam(true);
                          }}
                          disabled={isSubmitting}
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          Modifica
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="flex-1 text-xs"
                          onClick={() => handleDeleteTeam(standing.team_id)}
                          disabled={isSubmitting}
                        >
                          Elimina Team
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : leaderboard.length > 0 ? (
              <div className="space-y-3 stagger-children">
                {leaderboard.map((item) => (
                  <div key={item.team_id} className="space-y-2">
                    <LeaderboardCard
                      rank={item.rank}
                      teamName={item.team_name}
                      points={item.total_points}
                      owner={item.owner}
                      partner={item.partner}
                      isDuo={item.is_duo}
                      isCurrentUser={item.owner?.id === user?.id || item.partner?.id === user?.id}
                    />
                    {/* Mostra bottoni solo se sei il proprietario o partner del team */}
                    {(item.owner?.id === user?.id || item.partner?.id === user?.id) && (
                      <div className="flex gap-2 px-2">
                        <Button
                          size="sm"
                          variant="outline"
                          className="flex-1 text-xs"
                          onClick={() => {
                            const team = teams.find(t => t.id === item.team_id);
                            setTeamToEdit(team);
                            setEditTeamName(team.name);
                            setEditPartner(team.partner_id || 'none');
                            setShowEditTeam(true);
                          }}
                          disabled={isSubmitting}
                        >
                          <Settings className="w-3 h-3 mr-1" />
                          Modifica
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          className="flex-1 text-xs"
                          onClick={() => handleDeleteTeam(item.team_id)}
                          disabled={isSubmitting}
                        >
                          Elimina Team
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Card>
                <CardContent className="py-12">
                  <div className="text-center">
                    <Trophy className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Nessun team in classifica</h3>
                    <p className="text-gray-500">Crea il tuo team per iniziare!</p>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Giornate Tab */}
          <TabsContent value="giornate" className="p-4 max-w-4xl mx-auto">
            <GiornateTab 
              ref={giornateTabRef}
              leagueId={id}
              token={token}
              user={user}
              isAdmin={isAdmin}
              teams={teams}
              onMatchdayCalculated={handleMatchdayCalculated}
            />
          </TabsContent>

          {/* Professori Tab */}
          <TabsContent value="professori" className="p-4 max-w-2xl mx-auto">
            {isAdmin && (
              <div className="flex gap-2 mb-4">
                <Dialog open={showAddProfessor} onOpenChange={setShowAddProfessor}>
                  <DialogTrigger asChild>
                    <Button className="flex-1 rounded-full">
                      <Plus className="w-4 h-4 mr-2" />
                      Aggiungi Prof
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Aggiungi Professore</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleAddProfessor}>
                      <div className="space-y-4 py-4">
                        <div className="space-y-2">
                          <Label>Nome</Label>
                          <Input 
                            placeholder="es. Prof. Rossi" 
                            value={profName} 
                            onChange={(e) => setProfName(e.target.value)} 
                          />
                        </div>
                        <div className="space-y-2">
                          <Label>Materia</Label>
                          <Input 
                            placeholder="es. Matematica" 
                            value={profSubject} 
                            onChange={(e) => setProfSubject(e.target.value)} 
                          />
                        </div>
                      </div>
                      <DialogFooter>
                        <Button type="submit" disabled={isSubmitting}>
                          {isSubmitting ? 'Aggiunta...' : 'Aggiungi'}
                        </Button>
                      </DialogFooter>
                    </form>
                  </DialogContent>
                </Dialog>

                <Dialog open={showAddEvent} onOpenChange={(open) => {
                  setShowAddEvent(open);
                  if (!open) {
                    // Reset form quando il dialog viene chiuso
                    setSelectedProfessor('');
                    setSelectedEvent(null);
                    setEventMultiplier(1);
                  }
                }}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="flex-1 rounded-full">
                      <TrendingUp className="w-4 h-4 mr-2" />
                      Bonus/Malus
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="max-w-lg">
                    <DialogHeader>
                      <DialogTitle>Aggiungi Bonus/Malus</DialogTitle>
                      <DialogDescription>
                        Seleziona il professore, l'evento e la quantità
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                      <div className="space-y-2">
                        <Label>Professore</Label>
                        <Select value={selectedProfessor} onValueChange={setSelectedProfessor}>
                          <SelectTrigger>
                            <SelectValue placeholder="Seleziona professore" />
                          </SelectTrigger>
                          <SelectContent>
                            {professors.map(p => (
                              <SelectItem key={p.id} value={p.id}>
                                {p.name} ({p.subject})
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Quantità</Label>
                        <Input 
                          type="number" 
                          min="1" 
                          value={eventMultiplier} 
                          onChange={(e) => setEventMultiplier(parseInt(e.target.value) || 1)}
                          placeholder="es. 2 per doppia nota"
                        />
                        <p className="text-xs text-muted-foreground">
                          Inserisci la quantità (es: 2 per doppia nota, 3 per triplo voto, ecc.)
                        </p>
                      </div>
                      <div className="space-y-2">
                        <Label>Evento</Label>
                        <ScrollArea className="h-64 border rounded-lg p-2">
                          <div className="space-y-1">
                            <p className="text-xs font-semibold text-green-600 dark:text-green-400 px-2 py-1">BONUS</p>
                            {getBonusEvents().map((event, i) => (
                              <button
                                key={`bonus-${i}`}
                                type="button"
                                onClick={() => setSelectedEvent(event)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                  selectedEvent?.name === event.name
                                    ? 'bg-primary text-primary-foreground'
                                    : 'hover:bg-muted'
                                }`}
                              >
                                <span className="flex items-center justify-between">
                                  {event.name}
                                  <Badge variant="outline" className="ml-2 text-green-600">
                                    +{event.points}
                                    {selectedEvent?.name === event.name && eventMultiplier > 1 && (
                                      <span className="ml-1">× {eventMultiplier} = +{event.points * eventMultiplier}</span>
                                    )}
                                  </Badge>
                                </span>
                              </button>
                            ))}
                            <p className="text-xs font-semibold text-red-600 dark:text-red-400 px-2 py-1 mt-4">MALUS</p>
                            {getMalusEvents().map((event, i) => (
                              <button
                                key={`malus-${i}`}
                                type="button"
                                onClick={() => setSelectedEvent(event)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                                  selectedEvent?.name === event.name
                                    ? 'bg-primary text-primary-foreground'
                                    : 'hover:bg-muted'
                                }`}
                              >
                                <span className="flex items-center justify-between">
                                  {event.name}
                                  <Badge variant="outline" className="ml-2 text-red-600">
                                    {event.points}
                                    {selectedEvent?.name === event.name && eventMultiplier > 1 && (
                                      <span className="ml-1">× {eventMultiplier} = {event.points * eventMultiplier}</span>
                                    )}
                                  </Badge>
                                </span>
                              </button>
                            ))}
                          </div>
                        </ScrollArea>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleAddEvent} disabled={isSubmitting || !selectedProfessor || !selectedEvent}>
                        {isSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Aggiungi Evento'}
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            )}

            {professors.length > 0 ? (
              <div className="space-y-3">
                {professors.map((prof) => (
                  <Card key={prof.id} className="hover-lift">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                            <GraduationCap className="w-5 h-5 text-primary" />
                          </div>
                          <div>
                            <p className="font-semibold">{prof.name}</p>
                            <p className="text-sm text-muted-foreground">{prof.subject}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <div className="text-right">
                            <p className={`text-lg font-bold ${prof.total_points > 0 ? 'score-positive' : prof.total_points < 0 ? 'score-negative' : ''}`}>
                              {prof.total_points > 0 ? '+' : ''}{prof.total_points}
                            </p>
                            <p className="text-xs text-muted-foreground">punti</p>
                          </div>
                          {isAdmin && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                <DropdownMenuItem onClick={() => {
                                  setProfessorToEdit(prof);
                                  setEditProfName(prof.name);
                                  setEditProfSubject(prof.subject);
                                  setShowEditProfessor(true);
                                }}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Modifica
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleDeleteProfessor(prof.id, prof.name)}
                                  className="text-red-600"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Elimina
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                      {prof.assigned_to ? (
                        <div className="mt-2 pt-2 border-t space-y-2">
                          <Badge variant="secondary" className="text-xs">
                            Assegnato a: {teams.find(t => t.id === prof.assigned_to)?.name || 'Team sconosciuto'}
                          </Badge>
                          {isAdmin && (
                            <Button
                              size="sm"
                              variant="destructive"
                              className="w-full text-xs"
                              onClick={() => handleUnassignProfessor(prof.assigned_to, prof.id, prof.name)}
                              disabled={isSubmitting}
                            >
                              Rimuovi da Team
                            </Button>
                          )}
                        </div>
                      ) : isAdmin && (
                        <div className="mt-2 pt-2 border-t">
                          <Button
                            size="sm"
                            variant="outline"
                            className="w-full"
                            onClick={() => {
                              setProfessorToAssign(prof);
                              setShowAssignProfessor(true);
                            }}
                          >
                            <UserPlus className="w-4 h-4 mr-2" />
                            Assegna a Team
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <GraduationCap className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="font-semibold">Nessun professore</p>
                  <p className="text-sm text-muted-foreground">
                    {isAdmin ? 'Aggiungi i professori della classe' : "L'admin deve aggiungere i professori"}
                  </p>
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Membri Tab */}
          <TabsContent value="membri" className="p-4 max-w-2xl mx-auto">
            <div className="space-y-3">
              {league.members.map((member) => (
                <Card key={member.id}>
                  <CardContent className="p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10">
                        <AvatarImage src={member.avatar_url} />
                        <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                          {getInitials(member.name)}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-semibold flex items-center gap-2">
                          {member.name}
                          {member.id === user?.id && (
                            <Badge variant="outline" className="text-xs">Tu</Badge>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="flex gap-1">
                        {(league.owners?.includes(member.id) || member.id === league.owner_id) && (
                          <Badge className="bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                            <Crown className="w-3 h-3 mr-1" />
                            Owner
                          </Badge>
                        )}
                        {league.admins.includes(member.id) && !(league.owners?.includes(member.id) || member.id === league.owner_id) && (
                          <Badge variant="secondary">
                            <Shield className="w-3 h-3 mr-1" />
                            Admin
                          </Badge>
                        )}
                      </div>
                      {/* Show remove button for owner/admin, but not for self */}
                      {(isOwner || isAdmin) && member.id !== user?.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {/* Show remove admin option for owners only, if member is admin but not owner */}
                            {isOwner && league.admins.includes(member.id) && !(league.owners?.includes(member.id) || member.id === league.owner_id) && (
                              <DropdownMenuItem 
                                className="text-orange-600 dark:text-orange-400"
                                onClick={() => handleRemoveAdmin(member.id, member.name)}
                              >
                                <Shield className="mr-2 h-4 w-4" />
                                Rimuovi Admin
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              className="text-red-600 dark:text-red-400"
                              onClick={() => handleRemoveMember(member.id, member.name)}
                            >
                              <UserCog className="mr-2 h-4 w-4" />
                              Espelli dalla Lega
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Scambi Tab - FASE 3 */}
          <TabsContent value="scambi" className="p-4 max-w-2xl mx-auto">
            <TradesTab 
              leagueId={id}
              token={token}
              userId={user?.id}
              professors={professors}
              teams={teams}
              onTradeComplete={() => {
                fetchProfessors();
                fetchTeams();
                fetchLeaderboard();
              }}
            />
          </TabsContent>

          {/* Punti Tab */}
          <TabsContent value="punti" className="p-4 max-w-2xl mx-auto">
            <BonusMalusTab 
              leagueId={id}
              token={token}
              isAdmin={isAdmin}
              leagueName={league?.name}
            />
          </TabsContent>

          {/* Statistiche Tab */}
          <TabsContent value="statistiche" className="p-4 max-w-7xl mx-auto">
            <StatsTab 
              leagueId={id}
              token={token}
            />
          </TabsContent>

          {/* WhatsApp Tab */}
          {isOwner && (
            <TabsContent value="whatsapp" className="p-4 max-w-2xl mx-auto">
              <WhatsAppTab 
                leagueId={id}
                token={token}
                isOwner={isOwner}
              />
            </TabsContent>
          )}

          {/* Admin Tab */}
          {isAdmin && (
            <TabsContent value="admin">
              <AdminTab 
                leagueId={id}
                token={token}
                isAdmin={isAdmin}
                onDataChange={() => {
                  fetchAll();
                }}
              />
            </TabsContent>
          )}
        </Tabs>
      </main>

      <BottomNav />
    </div>
  );
};
